import { Check, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  ListItem,
  Avatar as MuiAvatar,
  Stack,
  Typography,
} from "@mui/joy";
import { useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ApiChallenge,
  ApiChallengeEntry,
  ApiChallengeEntryDetail,
  ApiChallengeEntryLog,
  ApiChallengeReward,
  ApiChallengeTier,
  ApiVenueInline,
  PaginatedResponse,
} from "griptonite-api-client";
import { sortBy } from "lodash";
import { useMemo, useState } from "react";

import Challenge from "@/assets/icons/Challenge";
import Warning from "@/assets/icons/Warning";
import {
  useChallengeEntry,
  useChallengeEntryLogs,
  useChallengeQuery,
  useManuallyVerifyChallengeEntryLogMutation,
  useRemoveChallengeEntryLogMutation,
  useRecordChallengeRedemptionMutation,
  useDeleteRewardRedemptionMutation,
} from "@/hooks/challenges";
import { getAbilityCategoryColor, getAbilityCategoryLabel } from "@/utils/abilityCategory";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import Avatar from "../Avatar";

type ChallengeEntryRecord =
  | {
      type: "log";
      log: ApiChallengeEntryLog;
    }
  | {
      type: "tierCompletion";
      tierCompletion: {
        timestamp: string;
        tier: ApiChallengeTier;
        redeemedRewards: ApiChallengeReward[];
      };
    };

const getChallengeEntryRecords = ({
  pages,
  challenge,
  entry,
}: {
  pages: PaginatedResponse<ApiChallengeEntryLog>[];
  challenge?: ApiChallenge;
  entry?: ApiChallengeEntry & Partial<ApiChallengeEntryDetail>;
}): ChallengeEntryRecord[] => {
  const logs = pages.flatMap((page) => page.data) ?? [];
  const logRecords = logs.map((log) => ({
    type: "log" as const,
    log,
  }));

  const tierCompletions = entry?.tierTimestamps
    ? (Object.entries(entry.tierTimestamps)
        .map(([tierUuid, timestamp]) => {
          const tier = challenge?.tiers.find((t) => t.uuid === tierUuid);
          // Get redeemed rewards for this tier
          const redeemedRewards =
            tier?.rewards.filter((reward) =>
              entry.rewardRedemptions?.some((redemption) => redemption.reward === reward.uuid),
            ) || [];

          return {
            timestamp,
            tier,
            redeemedRewards,
          };
        })
        .filter((x) => !!x.tier) as {
        timestamp: string;
        tier: ApiChallengeTier;
        redeemedRewards: ApiChallengeReward[];
      }[])
    : ([] as never[]);

  const tierCompletionRecords = tierCompletions.map((tc) => ({
    type: "tierCompletion" as const,
    tierCompletion: tc,
  }));

  return sortBy([...logRecords, ...tierCompletionRecords], (r) =>
    r.type === "log" ? r.log.creationTime : r.tierCompletion.timestamp,
  ).reverse();
};

const LogItem = ({
  log,
  challenge,
  venuesById,
  isExpanded = false,
  onClick,
  entryUuid,
}: {
  log: ApiChallengeEntryLog;
  challenge: ApiChallenge;
  venuesById: Record<string, ApiVenueInline>;
  isExpanded?: boolean;
  onClick?: () => void;
  entryUuid?: string;
}) => {
  // Initialize mutations
  const verifyLogMutation = useManuallyVerifyChallengeEntryLogMutation(challenge.uuid, entryUuid);
  const removeLogMutation = useRemoveChallengeEntryLogMutation(challenge.uuid, entryUuid);

  // Click handlers
  const handleManuallyVerifyLog = async () => {
    try {
      await verifyLogMutation.mutateAsync(log.uuid);
    } catch (error) {
      console.error("Failed to verify log:", error);
    }
  };

  const handleRemoveLog = async () => {
    try {
      await removeLogMutation.mutateAsync(log.uuid);
    } catch (error) {
      console.error("Failed to remove log:", error);
    }
  };
  const venue = venuesById[log.venue];
  return (
    <Stack
      sx={{
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
        padding: 1,
        backgroundColor: isExpanded ? "primary.50" : "inherit",
      }}
      spacing={1}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <MuiAvatar
          src={venue.logoUrl ? getThumbnailUrl(venue.logoUrl) || "" : ""}
          alt={venue.name}
          sx={{
            width: 40,
            height: 40,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "neutral.200",
            opacity: log.value === 0 ? 0.5 : 1,
          }}
        />
        <Box flex={1} sx={{ opacity: log.value === 0 ? 0.5 : 1 }}>
          <Typography level="title-sm" fontWeight="bold">
            {venuesById[log.venue]?.name || "Unknown Venue"}
          </Typography>
          <Typography level="body-xs" textColor="neutral.500">
            {format(new Date(log.creationTime), "MMM d, yyyy")}
            {"\u00A0\u00A0"}
            {format(new Date(log.creationTime), "h:mm a")}
          </Typography>
          {/* <Typography level="body-sm" textColor="neutral.600">
                {logDetails.details}
              </Typography>
              {logDetails.extraInfo && (
                <Typography level="body-xs" textColor="success.600">
                  {logDetails.extraInfo}
                </Typography>
              )} */}
        </Box>
        <Stack alignItems="flex-end" gap={0.5}>
          <Typography
            level="body-xs"
            textColor="neutral.700"
            fontWeight="bold"
            sx={{ opacity: log.value === 0 ? 0.5 : 1 }}
          >
            {log.data.attemptsCount}
            {log.data.attemptsCount < (challenge.settings.minimumRoutesPerSession || 0)
              ? `/${challenge.settings.minimumRoutesPerSession}`
              : null}{" "}
            routes logged
          </Typography>
          {log.manuallySet ? (
            <Typography level="body-xs" textColor="neutral.600">
              Manually verified
            </Typography>
          ) : !log.data.hasTagScanToday ? (
            <Typography
              level="body-xs"
              textColor="warning.500"
              startDecorator={<Warning fontSize="sm" />}
            >
              Did not scan tag
            </Typography>
          ) : null}
        </Stack>
        <IconButton size="sm" onClick={onClick}>
          <Edit fontSize="small" />
        </IconButton>
      </Stack>
      {isExpanded ? (
        <Stack direction="row" spacing={1}>
          {!log.value ? (
            <Button
              size="sm"
              onClick={handleManuallyVerifyLog}
              loading={verifyLogMutation.isPending}
              disabled={verifyLogMutation.isPending || removeLogMutation.isPending}
            >
              Manually verify
            </Button>
          ) : null}
          <Button
            size="sm"
            color="danger"
            variant="soft"
            onClick={handleRemoveLog}
            loading={removeLogMutation.isPending}
            disabled={verifyLogMutation.isPending || removeLogMutation.isPending}
          >
            Remove
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

const TierCompletionItem = ({
  tierCompletion,
  challenge,
  isExpanded = false,
  onClick,
  entryUuid,
}: {
  tierCompletion: {
    timestamp: string;
    tier: ApiChallengeTier;
    redeemedRewards: ApiChallengeReward[];
  };
  challenge?: ApiChallenge;
  isExpanded?: boolean;
  onClick?: () => void;
  entryUuid?: string;
}) => {
  // Initialize mutations
  const recordRedemptionMutation = useRecordChallengeRedemptionMutation(challenge?.uuid, entryUuid);
  const deleteRedemptionMutation = useDeleteRewardRedemptionMutation(challenge?.uuid, entryUuid);

  // Click handlers
  const handleMarkRewardRedeemed = async () => {
    const tier = tierCompletion.tier;
    if (!tier || tier.rewards.length === 0) return;

    try {
      // Mark all rewards in the tier as redeemed
      for (const reward of tier.rewards) {
        await recordRedemptionMutation.mutateAsync(reward.uuid);
      }
    } catch (error) {
      console.error("Failed to mark reward as redeemed:", error);
    }
  };

  const handleClearRedemption = async () => {
    const tier = tierCompletion.tier;
    if (!tier) return;

    try {
      // Clear all reward redemptions for the tier
      for (const reward of tier.rewards) {
        await deleteRedemptionMutation.mutateAsync(reward.uuid);
      }
    } catch (error) {
      console.error("Failed to clear redemption:", error);
    }
  };
  const isRedeemed = tierCompletion.redeemedRewards.length === tierCompletion.tier.rewards.length;
  return (
    <Stack
      sx={{
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
        padding: 1,
      }}
      spacing={1}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{
          backgroundColor: "#FFF6E5",
          margin: 1,
          padding: 1,
          borderRadius: 8,
        }}
      >
        <Challenge sx={{ color: "#FEC61A", width: 32, height: 32 }} />
        <Box flex={1}>
          <Typography level="title-md" fontWeight="bold">
            Tier {(challenge?.tiers.findIndex((t) => t.uuid === tierCompletion.tier.uuid) || 0) + 1}{" "}
            Completed
          </Typography>
          <Typography level="body-sm">
            {tierCompletion.tier.rewards.map((r) => r.name).join(", ") || "No rewards"}
          </Typography>
          <Typography level="body-xs">
            {format(new Date(tierCompletion.timestamp), "MMM d, yyyy")}
            {"\u00A0\u00A0"}
            {format(new Date(tierCompletion.timestamp), "h:mm a")}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" gap={0.5}>
          {tierCompletion.redeemedRewards.length === tierCompletion.tier.rewards.length ? (
            <Chip
              variant="solid"
              color="secondary"
              sx={{ fontWeight: "bold" }}
              startDecorator={<Check fontSize="small" />}
              size="sm"
            >
              Redeemed
            </Chip>
          ) : (
            <Chip variant="soft" color="danger" size="sm">
              Unclaimed
            </Chip>
          )}
        </Stack>
        <IconButton
          size="sm"
          onClick={onClick}
          sx={{
            "&:hover": {
              backgroundColor: "#0002",
            },
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Stack>
      {isExpanded ? (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            {isRedeemed ? (
              <Button
                size="sm"
                color="danger"
                variant="soft"
                onClick={handleClearRedemption}
                loading={deleteRedemptionMutation.isPending}
                disabled={recordRedemptionMutation.isPending || deleteRedemptionMutation.isPending}
              >
                Clear redemption
              </Button>
            ) : (
              <Button
                size="sm"
                color="primary"
                variant="solid"
                onClick={handleMarkRewardRedeemed}
                loading={recordRedemptionMutation.isPending}
                disabled={recordRedemptionMutation.isPending || deleteRedemptionMutation.isPending}
              >
                Mark reward redeemed
              </Button>
            )}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};

const LoadingSkeletons = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <ListItem key={i} sx={{ padding: 0 }}>
        <Card variant="outlined" sx={{ width: "100%" }}>
          <CardContent sx={{ padding: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography level="title-sm">
                  <Box
                    sx={{
                      backgroundColor: "neutral.200",
                      height: "1em",
                      width: "80px",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.4 },
                      },
                    }}
                  />
                </Typography>
                <Typography level="body-sm" sx={{ marginTop: 0.5 }}>
                  <Box
                    sx={{
                      backgroundColor: "neutral.200",
                      height: "0.8em",
                      width: "120px",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </Typography>
              </Box>
              <Stack alignItems="flex-end" gap={0.5}>
                <Box
                  sx={{
                    backgroundColor: "neutral.200",
                    height: "0.7em",
                    width: "60px",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    backgroundColor: "neutral.200",
                    height: "0.7em",
                    width: "50px",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </ListItem>
    ))}
  </>
);

export default function ChallengeEntrySidebar() {
  const { challengeUuid, entryUuid } = useParams({
    from: "/gym/$venueUuid/challenges/$challengeUuid/entries/$entryUuid",
  });

  const [expandedLogUuid, setExpandedLogUuid] = useState<string | null>(null);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useChallengeEntryLogs(challengeUuid, entryUuid);

  const { data: challenge } = useChallengeQuery(challengeUuid);

  const { data: entry } = useChallengeEntry(challengeUuid, entryUuid);

  const records = useMemo(
    () => getChallengeEntryRecords({ pages: data?.pages || [], challenge, entry }),
    [data?.pages, challenge, entry],
  );

  const venuesById = useMemo(() => {
    const result: Record<string, ApiVenueInline> = {};
    challenge?.venues.forEach((venue) => {
      result[venue.uuid] = venue;
    });
    return result;
  }, [challenge]);

  if (error) {
    return (
      <Box
        sx={{
          padding: 2,
          width: 400,
          minWidth: 400,
          borderLeft: "1px solid",
          borderColor: "neutral.200",
        }}
      >
        <Typography level="title-lg" textColor="danger.600">
          Error Loading Logs
        </Typography>
        <Typography level="body-sm" textColor="neutral.600" sx={{ marginTop: 1 }}>
          Failed to load challenge entry logs. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 400,
        minWidth: 400,
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid",
        borderColor: "neutral.200",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          padding: 1,
          borderBottom: "1px solid",
          borderBottomColor: "neutral.200",
        }}
        spacing={1.5}
      >
        <Avatar user={entry?.user} size={40} />
        <Box>
          <Typography level="title-md" fontWeight="bold">
            {entry?.user.firstName} {entry?.user.lastName}
          </Typography>
          {entry?.user.email ? (
            <Typography level="body-sm">
              <Link href={`mailto:${entry?.user.email}`}>{entry?.user.email}</Link>
            </Typography>
          ) : null}
          <Typography
            level="body-sm"
            fontWeight="bold"
            textColor={getAbilityCategoryColor(entry?.user.boulderAbilityCategory)}
          >
            {getAbilityCategoryLabel(entry?.user.boulderAbilityCategory)}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Stack sx={{ gap: 0, padding: 0 }}>
          {isLoading ? (
            <LoadingSkeletons />
          ) : records.length === 0 ? (
            <Stack spacing={1} padding={2}>
              <Typography level="body-md" textColor="neutral.500">
                No activity logs found
              </Typography>
              <Typography level="body-sm" textColor="neutral.400">
                This entry doesn&apos;t have any recorded activity yet.
              </Typography>
            </Stack>
          ) : challenge ? (
            records.map((record, index) =>
              record.type === "log" ? (
                <LogItem
                  key={`${record.log.uuid}-${index}`}
                  log={record.log}
                  challenge={challenge}
                  venuesById={venuesById}
                  isExpanded={record.log.uuid === expandedLogUuid}
                  onClick={() => setExpandedLogUuid(record.log.uuid)}
                  entryUuid={entryUuid}
                />
              ) : (
                <TierCompletionItem
                  challenge={challenge}
                  key={`${record.tierCompletion.tier.uuid}-${index}`}
                  tierCompletion={record.tierCompletion}
                  isExpanded={`tier-${record.tierCompletion.tier.uuid}` === expandedLogUuid}
                  onClick={() => setExpandedLogUuid(`tier-${record.tierCompletion.tier.uuid}`)}
                  entryUuid={entryUuid}
                />
              ),
            )
          ) : null}

          {isFetchingNextPage && (
            <ListItem sx={{ padding: 2, justifyContent: "center" }}>
              <CircularProgress size="sm" />
            </ListItem>
          )}
        </Stack>

        {hasNextPage && !isFetchingNextPage && (
          <Box sx={{ padding: 1 }}>
            <Button
              variant="outlined"
              color="neutral"
              fullWidth
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              Load More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
